import { SyntheticEvent, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';
import { extractPublicId } from '@/utils/media';
import { isMobileDevice, isIOS, getBrowserVideoAttributes } from '@/utils/videoCompatibility';
import { resolvePlayableSrc, resolvePlayableMp4Legacy } from '@/resolvers/media';
import { Play } from 'lucide-react';

interface OptimizedVideoProps {
  src: string;
  poster?: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  onPlay?: () => void;
  onError?: () => void;
}

export const OptimizedVideo = ({ 
  src, 
  poster, 
  className, 
  autoPlay = false, 
  muted = true, 
  loop = false, 
  controls = true,
  onPlay,
  onError
}: OptimizedVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  
  const mode = useConnectionMode();
  const isLiteMode = mode === 'lite';
  const isMobileEnv = isMobileDevice();
  const isIOSDevice = isIOS();
  
  // Extract public ID and resolve all video settings centrally
  const publicId = extractPublicId(src);
  
  // Centralized video resolution with all settings
  const { srcUrl, srcType, posterUrl, preload, shouldAutoPlay } = useMemo(() => {
    return resolvePlayableSrc({
      publicId,
      mode,
      ua: { isiOS: isIOSDevice },
      autoPlay,
      userHasInteracted
    });
  }, [publicId, mode, isIOSDevice, autoPlay, userHasInteracted]);

  // Memoize event handlers to prevent useEffect from running unnecessarily
  const handleLoadStart = useCallback(() => {
    console.log('[OptimizedVideo] Video load started');
    if (!isInitialized) setIsLoading(true);
  }, [isInitialized]);

  const handleLoadedData = useCallback(() => {
    console.log('[OptimizedVideo] Video first frame loaded');
    setIsLoading(false);
    if (!isInitialized) setIsInitialized(true);
    // In lite mode, if the user initiated the load, auto-play now that it's ready
    const v = videoRef.current;
    if (v && isLiteMode && userHasInteracted) {
      v.play().catch(err => console.warn('Autoplay after loadeddata failed:', err));
    }
  }, [isInitialized, isLiteMode, userHasInteracted]);

  const handleError = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    const videoElement = event.target as HTMLVideoElement;
    console.error('Video failed to load:', {
      src: srcUrl,
      originalSrc: src,
      error: videoElement.error,
      networkState: videoElement.networkState,
      readyState: videoElement.readyState
    });
    setHasError(true);
    setIsLoading(false);
    setIsInitialized(true);
    onError?.();
  }, [srcUrl, src, onError]);

  const handlePlay = useCallback(() => {
    onPlay?.();
  }, [onPlay]);

  // Auto-pause when video scrolls off-screen (Lite mode UX polish)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isLiteMode) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting && !video.paused) {
          video.pause();
        }
      },
      { threshold: 0.1 } // Pause when less than 10% visible
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [isLiteMode]);

  // Memoize video props to prevent unnecessary re-renders
  const videoProps = useMemo(() => ({
    ref: videoRef,
    className,
    poster: poster || posterUrl,
    muted: true, // Required for autoplay
    loop,
    playsInline: true, // Required for iOS
    'webkit-playsinline': 'true',
    crossOrigin: 'anonymous' as const,
    autoPlay: shouldAutoPlay,
    preload: preload,
    // In Lite mode, disable native controls until initialized to avoid competing buttons
    controls: isLiteMode ? isInitialized : true,
    ...getBrowserVideoAttributes(),
    style: {
      width: '100%',
      height: 'auto',
      touchAction: 'manipulation',
      WebkitTapHighlightColor: 'transparent'
    }
  }), [className, poster, posterUrl, loop, shouldAutoPlay, preload, isLiteMode, isInitialized]);

  if (hasError) {
    return (
      <div className={`${className} bg-muted flex items-center justify-center p-4 rounded-lg`}>
        <div className="text-center">
          <p className="text-muted-foreground text-sm mb-1">Video not found</p>
          <p className="text-xs text-muted-foreground/70">Upload may have failed</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" key={`${src}-video`}>
      <video 
        {...videoProps} 
        onLoadStart={handleLoadStart}
        onLoadedData={handleLoadedData}
        onError={handleError}
        onPlay={handlePlay}
        // Only add click handler when custom overlay is not showing
        {...(isLiteMode && !isInitialized ? {} : {
          onClick: (e) => {
            const video = e.currentTarget;
            setUserHasInteracted(true);
            e.preventDefault();
            e.stopPropagation();
            
            if (video.paused) {
              video.play().catch(console.error);
            } else {
              video.pause();
            }
          }
        })}
      >
        <source src={srcUrl} type={srcType} />
      </video>
      
      {/* Large play button overlay for Lite mode before load */}
      {isLiteMode && !isInitialized && (
        <div className="absolute inset-0 bg-black/10 flex items-center justify-center pointer-events-none">
          <div 
            className="bg-black/70 rounded-full p-6 pointer-events-auto cursor-pointer hover:bg-black/80 transition-colors"
            onClick={(e) => {
              const video = videoRef.current;
              if (video) {
                setUserHasInteracted(true);
                video.load();
                setIsLoading(true);
                video.play().catch(() => {/* fine, will play on loadeddata */});
              }
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Play className="w-8 h-8 text-white fill-white ml-1" />
          </div>
        </div>
      )}
      
      {/* Loading spinner - only show in standard mode, not Lite mode */}
      {isLoading && !isInitialized && !isLiteMode && (
        <div className="absolute inset-0 bg-muted/50 flex items-center justify-center pointer-events-none">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};